/* eslint-disable dot-notation */
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slide,
  Typography,
} from '@mui/material';
import { forwardRef, useMemo, useState } from 'react';
import { TransitionProps } from '@mui/material/transitions';
import {
  collection, getDocs, query, where,
} from 'firebase/firestore';
import Spinner from './Spinner';
import { useAppDispatch, useAppSelector } from '../store/Hooks';
import AlertUtil from '../utils/AlertUtil';
import ExportService from '../services/ExportService';
import { db } from '../FirebaseConfig';
import FormState from '../models/enums/FormState';

interface Props {
  setShow: any,
  show: boolean,
}

enum ExportType {
  DETAILED = 'detailed',
  NEXT_UPDATE = 'next-update',
}

const ALL_STATES = 'all';

const FORM_STATE_LABELS: Record<string, string> = {
  [FormState.DONE]: 'Terminé',
  [FormState.IN_PROGRESS]: 'En cours',
  [FormState.WAITING_RESULT]: 'En attente de résultat',
};

const Transition = forwardRef((
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) => <Slide direction="up" ref={ref} {...props} />);

const PromptExport: React.FC<Props> = ({ setShow, show }) => {
  const dispatch = useAppDispatch();
  const forms = useAppSelector((state) => state.user.forms) as any[] | null;

  const [loading, setLoading] = useState(false);
  const [exportType, setExportType] = useState<ExportType>(ExportType.DETAILED);
  const [formStateFilter, setFormStateFilter] = useState<string>(FormState.DONE);
  const [yearFilter, setYearFilter] = useState<string>(ALL_STATES);
  const [municipalityFilter, setMunicipalityFilter] = useState<string[]>([]);

  // Options de filtre dérivées des formulaires déjà chargés dans le store.
  const years = useMemo(() => {
    const set = new Set<number>();
    (forms ?? []).forEach((f) => {
      if (f.studiedYear != null) set.add(f.studiedYear);
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [forms]);

  const municipalities = useMemo(() => {
    const set = new Set<string>();
    (forms ?? []).forEach((f) => {
      if (f.municipalityName) set.add(f.municipalityName);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [forms]);

  const resetAndClose = () => {
    setShow(false);
  };

  const buildFileName = () => {
    const exportedDate = new Date().toLocaleDateString('fr-CA');
    if (exportType === ExportType.NEXT_UPDATE) {
      return `export-prochaines-mises-a-jour-${exportedDate}`;
    }
    return `export-formulaires-${exportedDate}`;
  };

  const fetchForms = async (): Promise<any[]> => {
    // On applique le filtre d'état côté serveur (une seule égalité, aucun index
    // composite requis) puis l'année et les municipalités côté client.
    const q = formStateFilter !== ALL_STATES
      ? query(collection(db, 'Forms'), where('state', '==', formStateFilter))
      : query(collection(db, 'Forms'));
    const querySnapshot = await getDocs(q);

    const selectedMunicipalities = new Set(municipalityFilter);
    const selectedYear = yearFilter !== ALL_STATES ? parseInt(yearFilter, 10) : null;

    return querySnapshot.docs
      .map((formsDoc: any) => ({ ...formsDoc.data(), formData: formsDoc.data().formData }))
      .filter((doc: any) => !!doc.formData)
      .filter((doc: any) => selectedYear === null || doc.studiedYear === selectedYear)
      .filter((doc: any) => (
        selectedMunicipalities.size === 0 || selectedMunicipalities.has(doc.formData.nomMunicipalite)
      ))
      .map((doc: any) => doc.formData);
  };

  // Année d'adhésion fiable par municipalité (source de vérité : la
  // municipalité, pas le formulaire).
  const fetchAdhesionYears = async (): Promise<Map<string, number>> => {
    const querySnapshot = await getDocs(query(collection(db, 'Municipalities')));
    const map = new Map<string, number>();
    querySnapshot.forEach((municipalityDoc: any) => {
      const { adhesionYearPEXBD } = municipalityDoc.data();
      if (adhesionYearPEXBD != null) {
        map.set(municipalityDoc.id, Number(adhesionYearPEXBD));
      }
    });
    return map;
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const formsData = await fetchForms();

      if (formsData.length === 0) {
        AlertUtil.createErrorAlert('Aucun formulaire ne correspond aux filtres sélectionnés', dispatch);
        return;
      }

      const fileName = buildFileName();
      const adhesionYearByMunicipalityId = await fetchAdhesionYears();
      if (exportType === ExportType.NEXT_UPDATE) {
        await ExportService.exportNextUpdateMatrixCSV(formsData, fileName, adhesionYearByMunicipalityId);
      } else {
        await ExportService.exportDetailedCSV(formsData, fileName, adhesionYearByMunicipalityId);
      }
      resetAndClose();
    } catch (e) {
      console.error(e);
      AlertUtil.createErrorAlert('Impossible d\'exporter les formulaires', dispatch);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeMunicipality = (event: any) => {
    const { value } = event.target;
    setMunicipalityFilter(typeof value === 'string' ? value.split(',') : value);
  };

  return (
    <>
      <Dialog
        open={show}
        onClose={resetAndClose}
        TransitionComponent={Transition}
        sx={{ backgroundColor: 'background.default !important' }}
      >
        <DialogTitle>Exporter les formulaires</DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ minWidth: '500px', margin: '0 0 15px 0' }}>
            <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Type de rapport</Typography>
                <RadioGroup
                  value={exportType}
                  onChange={(e) => setExportType(e.target.value as ExportType)}
                >
                  <FormControlLabel
                    value={ExportType.DETAILED}
                    control={<Radio />}
                    label="Détaillé (une ligne par indicateur)"
                  />
                  <FormControlLabel
                    value={ExportType.NEXT_UPDATE}
                    control={<Radio />}
                    label="Prochaines mises à jour (par municipalité)"
                  />
                </RadioGroup>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>État du formulaire</InputLabel>
                  <Select
                    label="État du formulaire"
                    value={formStateFilter}
                    onChange={(e) => setFormStateFilter(e.target.value)}
                  >
                    <MenuItem value={ALL_STATES}>Tous les états</MenuItem>
                    {Object.values(FormState).map((state) => (
                      <MenuItem key={state} value={state}>{FORM_STATE_LABELS[state] ?? state}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Année étudiée</InputLabel>
                  <Select
                    label="Année étudiée"
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                  >
                    <MenuItem value={ALL_STATES}>Toutes les années</MenuItem>
                    {years.map((year) => (
                      <MenuItem key={year} value={String(year)}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Municipalités (toutes par défaut)</InputLabel>
                  <Select
                    multiple
                    label="Municipalités (toutes par défaut)"
                    value={municipalityFilter}
                    onChange={handleChangeMunicipality}
                    renderValue={(selected: string[]) => (selected.length === 0 ? 'Toutes' : selected.join(', '))}
                  >
                    {municipalities.map((name) => (
                      <MenuItem key={name} value={name}>
                        <ListItemIcon>
                          <Checkbox checked={municipalityFilter.indexOf(name) > -1} />
                        </ListItemIcon>
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px',
          }}>
            <Button variant="outlined" color="error" sx={{ mt: 1, mb: 1 }} onClick={resetAndClose}>
              Annuler
            </Button>
            <Button variant="contained" sx={{ mt: 1, mb: 1 }} onClick={handleExport}>
              Exporter
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Spinner show={loading} />
    </>
  );
};

export default PromptExport;
